import { MCard, MSpinner } from "components/Shared";
import { EmployeeDetailDto } from "interfaces/Mueller.Business.Models";
import React, { useEffect, useRef, useState } from "react";
import { getEmployeeDetailsByEmployeeId, updateEmployeeDetailsByEmployeeId } from "requests/employee";
import responseHandler from "responseHandler";
import { connect } from "react-redux";
import { MCardControlMethods, MCardField } from "components/Shared/MCard";
import { userHasRight } from "selectors/rights";
import { OPERATIONS_DIRECTOR_PROFILE_EDIT, OPERATIONS_MANAGER_PROFILE_EDIT } from "typeConstants/rights";
import { FIELD_OPERATIONS_MANAGER, LENDER_LOGISTICS_MANAGER } from "typeConstants/employeeRoles";
import { DATA_TYPE_DATE } from "typeConstants/dataTypes";
import * as yup from "yup";

interface OperationsManagementEmployeeDetailsProps {
  employeeId: number;
  //state
  currentUserInternetUserId: number;
  canEditOpsDirectorProfiles: boolean;
  canEditOpsManagerProfiles: boolean;
  //dispatch
  makeErrorToast: (text: string) => void;
  makeSuccessToast: (text: string) => void;
}

const OperationsManagementEmployeeDetails = (props: OperationsManagementEmployeeDetailsProps) => {

  const [employeeDetails, setEmployeeDetails] = useState<EmployeeDetailDto | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const mounted = useRef(true);
  const cardControlMethods = useRef<MCardControlMethods>(null);

  const getDetails = () => {
    getEmployeeDetailsByEmployeeId(
      props.employeeId,
      (details: EmployeeDetailDto) => {
        setEmployeeDetails(details);
        setIsFetching(false);
      },
      mounted,
      () => {
        props.makeErrorToast("Failed to fetch employee details");
      }
    );
  };

  useEffect(() => {
    setIsFetching(true);
    getDetails();
  }, [props.employeeId]);

  if (isFetching || !employeeDetails) {
    return <MSpinner />;
  }

  const isViewingSelf = props.currentUserInternetUserId === employeeDetails?.internetUserId;
  const isViewingOpsManager = [FIELD_OPERATIONS_MANAGER, LENDER_LOGISTICS_MANAGER].includes(employeeDetails?.roleId); //if not viewing an OM assume they're viewing an OD since those are the only roles in the ops management list
  const canEditExtensionAndBirthday = isViewingSelf || (props.canEditOpsDirectorProfiles && !isViewingOpsManager) || (props.canEditOpsManagerProfiles && isViewingOpsManager);

  const fields: MCardField[] = [
    { label: "First Name", value: employeeDetails?.firstName, editable: false },
    { label: "Last Name", value: employeeDetails?.lastName, editable: false },
    { label: "Extension", value: employeeDetails?.extension, editable: canEditExtensionAndBirthday },
    { label: "Email", value: employeeDetails?.email, editable: false },
    { label: "Hire Date", value: employeeDetails?.hireDate, dataType: DATA_TYPE_DATE, editable: false },
    { label: "Role", value: employeeDetails?.role?.name, editable: false },
    { label: "Address", value: employeeDetails?.address, editable: false },
    { label: "City", value: employeeDetails?.city, editable: false },
    { label: "State", value: employeeDetails?.state, editable: false },
    { label: "Zip", value: employeeDetails?.postalCode, editable: false },
    { label: "Phone", value: employeeDetails?.homePhone, editable: false },
    { label: "Status", value: employeeDetails?.isActive ? "Active" : "Inactive", editable: false },
    { label: "# of Employees", value: employeeDetails?.supervisedEmployeesCount, editable: false },
    { label: "Tenure", value: `${employeeDetails?.tenure.toLocaleUpperCase()}`, editable: false },
    { label: "Birthday (MM/DD)", key: "Birthday", value: employeeDetails?.birthDate, editable: canEditExtensionAndBirthday, validation: yup.string().nullable().max(5, "Birthday cannot be more than 5 characters").matches(/^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/, "Birthday must be format of MM/DD") }
  ];

  const onSubmitEdits = () => {
    if (cardControlMethods.current.runValidation()) {
      setIsFetching(true);
      updateEmployeeDetailsByEmployeeId(
        props.employeeId,
        cardControlMethods.current.getEditedData(),
        () => {
          props.makeSuccessToast("Employee details updated successfully");
          getDetails();
        },
        mounted,
        () => {
          props.makeErrorToast("Failed to update employee details, please try again");
          setIsFetching(false);
        }
      );
    }
  };

  return (
    <MCard
      title="HR Information"
      fields={fields}
      isEditable={canEditExtensionAndBirthday}
      getControlMethods={(cm) => cardControlMethods.current = cm}
      onEditFinished={onSubmitEdits}
      isFetching={isFetching}
    />
  );
};

const mapStateToProps = state => ({
  currentUserInternetUserId: state.user.profile.internetUserId,
  canEditOpsDirectorProfiles: userHasRight(OPERATIONS_DIRECTOR_PROFILE_EDIT)(state),
  canEditOpsManagerProfiles: userHasRight(OPERATIONS_MANAGER_PROFILE_EDIT)(state),
});

const mapDispatchToProps = dispatch => ({
  makeSuccessToast(text: string) {
    responseHandler.customToast(text, "success", dispatch);
  },
  makeErrorToast(text: string) {
    responseHandler.customToast(text, "error", dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(OperationsManagementEmployeeDetails);